export type VerifiedPrivateTradingScope = Readonly<{ tenantId: string; userId: string }>;
export type PrivateStorageArea = 'local' | 'session';

type StorageEnvelope<T = unknown> = {
  version: 1;
  tenantId: string;
  userId: string;
  value: T;
};

const PRIVATE_STORAGE_PREFIX = 'trade-proof:private:v1';
const PRIVATE_SCOPE_CHANGED_EVENT = 'trade-proof-private-scope-changed';

let activeScope: VerifiedPrivateTradingScope | null = null;
let scopeGeneration = 0;
const listeners = new Set<() => void>();

function normalizeScope(scope: VerifiedPrivateTradingScope | null | undefined): VerifiedPrivateTradingScope | null {
  const tenantId = String(scope?.tenantId ?? '').trim();
  const userId = String(scope?.userId ?? '').trim();
  return tenantId && userId ? Object.freeze({ tenantId, userId }) : null;
}
function sameScope(left: VerifiedPrivateTradingScope | null, right: VerifiedPrivateTradingScope | null): boolean {
  return left?.tenantId === right?.tenantId && left?.userId === right?.userId;
}

function announceScopeChange(): void {
  scopeGeneration += 1;
  for (const listener of listeners) {
    try { listener(); } catch { /* Isolation notifications must not break auth transitions. */ }
  }
  try {
    window.dispatchEvent(new CustomEvent(PRIVATE_SCOPE_CHANGED_EVENT, {
      detail: { ready: activeScope !== null, generation: scopeGeneration },
    }));
  } catch { /* Storage is unavailable during SSR and some tests. */ }
}

/** Call only after the authenticated tenant bootstrap succeeds and its user is verified. */
export function activatePrivateTradingScope(scope: VerifiedPrivateTradingScope): boolean {
  const next = normalizeScope(scope);
  if (!next) return false;
  if (sameScope(activeScope, next)) return true;
  activeScope = next;
  announceScopeChange();
  return true;
}

/** Fail closed immediately on logout, identity change, or tenant-bootstrap restart/failure. */
export function invalidatePrivateTradingScope(): void {
  if (!activeScope) return;
  activeScope = null;
  announceScopeChange();
}

export function getVerifiedPrivateTradingScope(): VerifiedPrivateTradingScope | null {
  return activeScope;
}

export function getPrivateTradingScopeGeneration(): number {
  return scopeGeneration;
}

export function isPrivateTradingScopeCurrent(scope: VerifiedPrivateTradingScope | null, generation: number): boolean {
  return Boolean(scope && sameScope(scope, activeScope) && generation === scopeGeneration);
}

export function subscribePrivateTradingScope(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function privateTradingScopeChangedEventName(): string {
  return PRIVATE_SCOPE_CHANGED_EVENT;
}

export function privateTradingStorageKey(domain: string, area: PrivateStorageArea = 'local'): string | null {
  const scope = activeScope;
  const cleanDomain = String(domain ?? '').trim();
  if (!scope || !cleanDomain) return null;
  return [PRIVATE_STORAGE_PREFIX, area, encodeURIComponent(scope.tenantId), encodeURIComponent(scope.userId), encodeURIComponent(cleanDomain)].join(':');
}

function getStorage(area: PrivateStorageArea): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return area === 'session' ? window.sessionStorage : window.localStorage;
  } catch { return null; }
}

function parseEnvelope(raw: string | null, scope: VerifiedPrivateTradingScope): { found: boolean; value?: unknown } {
  if (raw == null) return { found: false };
  try {
    const parsed = JSON.parse(raw) as Partial<StorageEnvelope>;
    if (parsed?.version !== 1 || parsed.tenantId !== scope.tenantId || parsed.userId !== scope.userId || !Object.prototype.hasOwnProperty.call(parsed, 'value')) {
      return { found: false };
    }
    return { found: true, value: parsed.value };
  } catch { return { found: false }; }
}

export function readPrivateTradingValue<T = unknown>(domain: string, area: PrivateStorageArea = 'local'): T | null {
  const scope = activeScope;
  const key = privateTradingStorageKey(domain, area);
  const storage = getStorage(area);
  if (!scope || !key || !storage) return null;
  try {
    const result = parseEnvelope(storage.getItem(key), scope);
    return result.found ? result.value as T : null;
  } catch { return null; }
}

export function writePrivateTradingValue(domain: string, value: unknown, area: PrivateStorageArea = 'local'): boolean {
  const scope = activeScope;
  const generation = scopeGeneration;
  const key = privateTradingStorageKey(domain, area);
  const storage = getStorage(area);
  if (!scope || !key || !storage) return false;
  try {
    const serialized = JSON.stringify({ version: 1, tenantId: scope.tenantId, userId: scope.userId, value } satisfies StorageEnvelope);
    if (!isPrivateTradingScopeCurrent(scope, generation)) return false;
    storage.setItem(key, serialized);
    return isPrivateTradingScopeCurrent(scope, generation) && storage.getItem(key) === serialized;
  } catch { return false; }
}

export function removePrivateTradingValue(domain: string, area: PrivateStorageArea = 'local'): boolean {
  const scope = activeScope;
  const generation = scopeGeneration;
  const key = privateTradingStorageKey(domain, area);
  const storage = getStorage(area);
  if (!scope || !key || !storage) return false;
  try {
    if (!isPrivateTradingScopeCurrent(scope, generation)) return false;
    storage.removeItem(key);
    return isPrivateTradingScopeCurrent(scope, generation);
  } catch { return false; }
}

/** Compatibility adapter for legacy code that stores JSON strings directly. */
export function readPrivateTradingJson(domain: string, area: PrivateStorageArea = 'local', legacyKeys: readonly string[] = []): string | null {
  const value = readPrivateTradingValue<unknown>(domain, area)
    ?? (legacyKeys.length ? migrateOwnedLegacyPrivateValue<unknown>(domain, legacyKeys, area) : null);
  if (value == null) return null;
  try { return JSON.stringify(value); } catch { return null; }
}

export function writePrivateTradingJson(domain: string, raw: string, area: PrivateStorageArea = 'local'): boolean {
  try { return writePrivateTradingValue(domain, JSON.parse(raw), area); } catch { return false; }
}

function rowProvesOwnership(value: unknown, scope: VerifiedPrivateTradingScope): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  const userId = String(record.userId ?? record.user_id ?? '').trim();
  const tenantId = String(record.tenantId ?? record.tenant_id ?? '').trim();
  return userId === scope.userId && tenantId === scope.tenantId;
}

/**
 * Copy a legacy record only when ownership is explicit on every record.
 * The legacy key is deliberately never removed or rewritten.
 */
export function migrateOwnedLegacyPrivateValue<T = unknown>(
  domain: string,
  legacyKeys: readonly string[],
  area: PrivateStorageArea = 'local',
): T | null {
  const scope = activeScope;
  const destinationKey = privateTradingStorageKey(domain, area);
  const storage = getStorage(area);
  if (!scope || !destinationKey || !storage) return null;
  const existing = parseEnvelope(storage.getItem(destinationKey), scope);
  if (existing.found) return existing.value as T;

  for (const legacyKey of legacyKeys) {
    try {
      const raw = storage.getItem(String(legacyKey ?? '').trim());
      if (!raw) continue;
      const value: unknown = JSON.parse(raw);
      const records = Array.isArray(value) ? value : [value];
      if (!records.length || !records.every((record) => rowProvesOwnership(record, scope))) continue;
      const generation = scopeGeneration;
      if (!isPrivateTradingScopeCurrent(scope, generation)) return null;
      const serialized = JSON.stringify({ version: 1, tenantId: scope.tenantId, userId: scope.userId, value } satisfies StorageEnvelope);
      storage.setItem(destinationKey, serialized);
      if (!isPrivateTradingScopeCurrent(scope, generation) || storage.getItem(destinationKey) !== serialized) return null;
      return value as T;
    } catch { /* Keep malformed or unavailable legacy data untouched. */ }
  }
  return null;
}
